import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import type { ModelComponentQueryResult } from "@/types/supabase-helpers";

type ProductModel = Tables<"product_models">;

const formSchema = z.object({
  name: z.string().min(1, "Nimi on pakollinen"),
  description: z.string().optional(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface ProductModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  model: ProductModel | null;
}

export function ProductModelDialog({
  open,
  onOpenChange,
  model,
}: ProductModelDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = !!model;
  const [selectedTypeId, setSelectedTypeId] = useState<string>("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      is_active: true,
    },
  });

  useEffect(() => {
    if (model) {
      form.reset({
        name: model.name,
        description: model.description || "",
        is_active: model.is_active,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        is_active: true,
      });
    }
  }, [model, form]);

  // Fetch model components
  const { data: modelComponents = [] } = useQuery<ModelComponentQueryResult[]>({
    queryKey: ["model_components", model?.id],
    queryFn: async () => {
      if (!model?.id) return [];
      const { data, error } = await supabase
        .from("model_components")
        .select(`
          *,
          component_type:component_types(*),
          default_material:materials(*)
        `)
        .eq("product_model_id", model.id)
        .order("position");
      if (error) throw error;
      return data as ModelComponentQueryResult[];
    },
    enabled: !!model?.id,
  });

  // Fetch all component types
  const { data: componentTypes = [] } = useQuery({
    queryKey: ["component_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("component_types")
        .select("*")
        .is("deleted_at", null)
        .order("position");
      if (error) throw error;
      return data;
    },
  });

  // Fetch all materials
  const { data: materials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("*")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Save product model
  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isEdit) {
        const { error } = await supabase
          .from("product_models")
          .update({
            name: values.name,
            description: values.description || null,
            is_active: values.is_active,
          })
          .eq("id", model.id);
        if (error) throw error;
      } else {
        // Get max position
        const { data: maxPosRows } = await supabase
          .from("product_models")
          .select("position")
          .is("deleted_at", null)
          .order("position", { ascending: false })
          .limit(1);

        const maxPos = maxPosRows?.[0] ?? null;

        const { error } = await supabase.from("product_models").insert({
          name: values.name,
          description: values.description || null,
          is_active: values.is_active,
          position: (maxPos?.position || 0) + 1,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_models"] });
      toast.success(isEdit ? "Tuotemalli päivitetty" : "Tuotemalli luotu");
      onOpenChange(false);
    },
    onError: () => toast.error("Tallennus epäonnistui"),
  });

  // Add component to model
  const addComponentMutation = useMutation({
    mutationFn: async (componentTypeId: string) => {
      if (!model?.id) return;

      const { error } = await supabase.from("model_components").insert({
        product_model_id: model.id,
        component_type_id: componentTypeId,
        min_quantity: 0,
        max_quantity: 1,
        position: modelComponents.length,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["model_components", model?.id] });
      toast.success("Komponentti lisätty");
    },
    onError: (error: Error) => toast.error(error.message || "Lisäys epäonnistui"),
  });

  // Remove component from model
  const removeComponentMutation = useMutation({
    mutationFn: async (componentId: string) => {
      const { error } = await supabase
        .from("model_components")
        .delete()
        .eq("id", componentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["model_components", model?.id] });
      toast.success("Komponentti poistettu");
    },
    onError: () => toast.error("Poisto epäonnistui"),
  });

  // Update component
  const updateComponentMutation = useMutation({
    mutationFn: async ({
      componentId,
      updates,
    }: {
      componentId: string;
      updates: { 
        default_material_id?: string | null; 
        min_quantity?: number; 
        max_quantity?: number;
        default_width?: number | null;
        default_height?: number | null;
        default_thickness?: number | null;
        label?: string | null;
      };
    }) => {
      const { error } = await supabase
        .from("model_components")
        .update(updates)
        .eq("id", componentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["model_components", model?.id] });
    },
    onError: () => toast.error("Päivitys epäonnistui"),
  });

  const onSubmit = (values: FormValues) => {
    saveMutation.mutate(values);
  };

  // Available component types (not yet added)
  const availableTypes = componentTypes;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Muokkaa tuotemallia" : "Uusi tuotemalli"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nimi</FormLabel>
                  <FormControl>
                    <Input placeholder="Esim. Hautakivi Classic" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kuvaus</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Tuotemallin kuvaus..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <Label>Aktiivinen</Label>
                </FormItem>
              )}
            />

            {isEdit && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Mallin komponentit</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {modelComponents.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      Ei komponentteja. Lisää komponentteja alla olevasta valikosta.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {modelComponents.map((mc) => (
                        <div
                          key={mc.id}
                          className="flex items-center gap-3 p-3 border rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {mc.component_type?.label || mc.component_type?.name}
                              </p>
                              {mc.label && (
                                <Badge variant="outline" className="text-xs">{mc.label}</Badge>
                              )}
                            </div>
                            <Input
                              defaultValue={mc.label || ""}
                              onBlur={(e) => {
                                if (e.target.value !== (mc.label || "")) {
                                  updateComponentMutation.mutate({
                                    componentId: mc.id,
                                    updates: { label: e.target.value || null },
                                  });
                                }
                              }}
                              placeholder="Tarkenne (esim. vasen, oikea, iso...)"
                              className="h-8 text-sm mt-1"
                            />
                            <div className="flex gap-4 mt-2">
                              <div className="flex items-center gap-2">
                                <Label className="text-xs">Min:</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  className="w-16 h-8"
                                  value={mc.min_quantity}
                                  onChange={(e) =>
                                    updateComponentMutation.mutate({
                                      componentId: mc.id,
                                      updates: { min_quantity: parseInt(e.target.value) || 0 },
                                    })
                                  }
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-xs">Max:</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  className="w-16 h-8"
                                  value={mc.max_quantity}
                                  onChange={(e) =>
                                    updateComponentMutation.mutate({
                                      componentId: mc.id,
                                      updates: { max_quantity: parseInt(e.target.value) || 1 },
                                    })
                                  }
                                />
                              </div>
                                <div className="flex items-center gap-2">
                                <Label className="text-xs">Oletusmateriaali:</Label>
                                <Select
                                  value={mc.default_material_id || "none"}
                                  onValueChange={(value) =>
                                    updateComponentMutation.mutate({
                                      componentId: mc.id,
                                      updates: {
                                        default_material_id: value === "none" ? null : value,
                                      },
                                    })
                                  }
                                >
                                  <SelectTrigger className="w-40 h-8">
                                    <SelectValue placeholder="Valitse" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Ei oletusta</SelectItem>
                                    {materials.map((m) => (
                                      <SelectItem key={m.id} value={m.id}>
                                        {m.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            {/* Oletusmitat (mm) */}
                            <div className="flex gap-4 mt-2">
                              <div className="flex items-center gap-2">
                                <Label className="text-xs whitespace-nowrap">Leveys (mm):</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step={1}
                                  className="w-20 h-8"
                                  value={mc.default_width ? Math.round(mc.default_width * 1000) : ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateComponentMutation.mutate({
                                      componentId: mc.id,
                                      updates: { default_width: e.target.value ? parseFloat(e.target.value) / 1000 : null },
                                    })
                                  }
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-xs whitespace-nowrap">Korkeus (mm):</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step={1}
                                  className="w-20 h-8"
                                  value={mc.default_height ? Math.round(mc.default_height * 1000) : ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateComponentMutation.mutate({
                                      componentId: mc.id,
                                      updates: { default_height: e.target.value ? parseFloat(e.target.value) / 1000 : null },
                                    })
                                  }
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-xs whitespace-nowrap">Paksuus (mm):</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step={1}
                                  className="w-20 h-8"
                                  value={mc.default_thickness ? Math.round(mc.default_thickness * 1000) : ""}
                                  placeholder="-"
                                  onChange={(e) =>
                                    updateComponentMutation.mutate({
                                      componentId: mc.id,
                                      updates: { default_thickness: e.target.value ? parseFloat(e.target.value) / 1000 : null },
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeComponentMutation.mutate(mc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {componentTypes.length > 0 && (
                    <div className="flex gap-2 pt-2">
                      <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Lisää komponentti..." />
                        </SelectTrigger>
                        <SelectContent>
                          {componentTypes.map((ct) => (
                            <SelectItem key={ct.id} value={ct.id}>
                              {ct.label || ct.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={!selectedTypeId}
                        onClick={() => {
                          addComponentMutation.mutate(selectedTypeId);
                          setSelectedTypeId("");
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Peruuta
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Tallennetaan..." : "Tallenna"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
